"use client";
import React, { useRef } from "react";
import { typographyClass } from "../typography";
import { cn, priceFormatter } from "@/lib/utils";
import { AccordionTrigger } from "./menu-accordion";
import { Accordion, AccordionContent, AccordionItem } from "../ui/accordion";
import * as AccordionPrimitive from "@radix-ui/react-accordion";

import { ChevronDown } from "lucide-react";
import Allergens from "../allergens/allergens";
import { MenuProps } from "./menu";
import { BlurFade } from "../ui/blur-fade";
import { RichText } from "@payloadcms/richtext-lexical/react";
import { useTranslations } from "next-intl";
import { convertTextToTextWithBr } from "@/lib/ui-utils";

export default function MenuMobile({ menu }: MenuProps) {
  const t = useTranslations("menu");
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const onAccordionChanged = (value: string): void => {
    const ref = itemRefs.current[value];
    if (!ref) return;

    setTimeout(() => {
      const yOffset = -90;
      const y = ref.getBoundingClientRect().top + window.pageYOffset + yOffset;

      window.scrollTo({
        top: y,
        behavior: "smooth",
      });
    }, 250);
  };

  return (
    <div className="gap-10 w-full flex md:hidden">
      <div className="w-full flex flex-col gap-14">
        <Accordion type="single" collapsible onValueChange={onAccordionChanged}>
          {menu?.dishGroup?.map((dishGroup, index) => {
            return (
              <section
                id={t(dishGroup.type).toLowerCase().split(" ").join("-")}
                key={index}
              >
                <BlurFade
                  delay={0.1 * index}
                  className={cn(
                    "w-full xl:w-1/2 flex flex-col mx-auto styled-receipt border rounded-xl border-muted/30 scroll-mt-[76px]",
                    {
                      "mt-8": index != 0,
                    }
                  )}
                >
                  <AccordionItem
                    ref={(el) => {
                      itemRefs.current[`accordion-${index}`] = el;
                    }}
                    value={`accordion-${index}`}
                    className="border-0"
                  >
                    <AccordionPrimitive.Header asChild>
                      <h2 className={cn()}>
                        <AccordionPrimitive.Trigger
                          className={cn(
                            typographyClass.subtitle,
                            "w-full flex flex-1 items-center justify-between transition-all [&[data-state=open]>svg]:rotate-180",
                            "uppercase text-primary !font-medium text-center",
                            "px-4 py-3"
                          )}
                        >
                          {t(dishGroup.type)}
                          <ChevronDown className="h-[24px] w-[24px] shrink-0 text-muted-foreground transition-transform duration-200" />
                        </AccordionPrimitive.Trigger>
                      </h2>
                    </AccordionPrimitive.Header>

                    <AccordionContent className="pb-0">
                      <div
                        className={cn("grid grid-cols-1", {
                          "!grid-cols-1": dishGroup.category?.length == 1,
                        })}
                      >
                        <Accordion
                          type="single"
                          collapsible
                          onValueChange={onAccordionChanged}
                        >
                          {dishGroup.category?.map(
                            (category, categoryIndex) => {
                              const child = (
                                <div className="border-t border-muted/10 flex flex-col py-2">
                                  {category.dishes?.map((dish, dishIndex) => (
                                    <div
                                      className={cn(
                                        "flex flex-row py-3.5 gap-5",
                                        {
                                          "pt-2": dishIndex == 0,
                                        }
                                      )}
                                      key={`dish-${categoryIndex}-${dishIndex}`}
                                    >
                                      <div className="flex flex-col w-full px-4">
                                        <div>
                                          <p
                                            className={cn(
                                              typographyClass.largeBody,
                                              "uppercase font-semibold inline text-primary"
                                            )}
                                          >
                                            {dish.name}
                                          </p>

                                          {(dish.priceBrackets == null ||
                                            dish.priceBrackets!.length < 1) && (
                                            <p
                                              className={cn(
                                                typographyClass.largeBody,
                                                "inline ml-4"
                                              )}
                                            >
                                              {priceFormatter.format(
                                                dish.price!
                                              )}
                                            </p>
                                          )}
                                        </div>

                                        {dish.priceBrackets != null &&
                                          dish.priceBrackets.map(
                                            (
                                              priceBracket,
                                              priceBracketIndex
                                            ) => (
                                              <div
                                                className="flex items-end"
                                                key={`${index}-${priceBracketIndex}`}
                                              >
                                                <span className="text-muted">
                                                  {priceBracket.description}
                                                </span>
                                                <p
                                                  className={cn(
                                                    typographyClass.largeBody,
                                                    "ml-4"
                                                  )}
                                                >
                                                  {priceFormatter.format(
                                                    priceBracket.price!
                                                  )}
                                                </p>
                                              </div>
                                            )
                                          )}
                                        <span className="text-muted">
                                          {dish.description}
                                        </span>
                                        {dish.allergens &&
                                          dish.allergens?.length > 0 && (
                                            <Allergens
                                              isMobile
                                              className="my-1 w-fit"
                                              allergens={dish.allergens?.toSorted()}
                                            />
                                          )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              );
                              return (
                                <div
                                  className={cn("flex flex-col w-full", {
                                    "xl:border-l border-muted/10":
                                      categoryIndex % 2 != 0,
                                  })}
                                  key={`dish-${categoryIndex}`}
                                >
                                  {category.name ? (
                                    <AccordionItem
                                      ref={(el) => {
                                        itemRefs.current[
                                          `accordion-${index}-${categoryIndex}`
                                        ] = el;
                                      }}
                                      value={`accordion-${index}-${categoryIndex}`}
                                      className="border-b-0"
                                    >
                                      <AccordionTrigger asChild>
                                        <div
                                          className={cn(
                                            "border-t px-4 py-3 border-muted/10",
                                            typographyClass.subtitle,
                                            "uppercase font-medium text-center"
                                          )}
                                        >
                                          {t(category.name!)}
                                          <ChevronDown className="h-6 w-6 shrink-0 text-muted transition-transform duration-200" />
                                        </div>
                                      </AccordionTrigger>
                                      <AccordionContent
                                        asChild
                                        className="pb-0"
                                      >
                                        {child}
                                      </AccordionContent>
                                    </AccordionItem>
                                  ) : (
                                    child
                                  )}
                                </div>
                              );
                            }
                          )}
                        </Accordion>
                      </div>
                      {dishGroup.extra &&
                        dishGroup.extra.root.children.filter(
                          (el) => (el.children as object[]).length > 0
                        ).length > 0 && (
                          <div className="border-t border-muted/10 py-4">
                            <div className="mx-8">
                              <RichText
                                className={cn(
                                  typographyClass.largeBody,
                                  "text-center text-balance"
                                )}
                                data={dishGroup!.extra!}
                              />
                            </div>
                          </div>
                        )}
                    </AccordionContent>
                  </AccordionItem>
                </BlurFade>
              </section>
            );
          })}
        </Accordion>
        <BlurFade
          className="bg-muted/10 p-4 rounded-xl"
          delay={0.1 * menu!.dishGroup!.length}
        >
          <p>{convertTextToTextWithBr(t("desclaimer"))}</p>
        </BlurFade>
      </div>
    </div>
  );
}
